<template>
    <div class="dashboard-body-futuristic">
        <!-- Main Container matches Dashboard -->
        <main class="futuristic-container">
            <!-- Sidebar -->
            <AppSidebar activeItem="active" />

            <!-- Main Content Area -->
            <section class="dashboard-card-futuristic dashboard-card-scrollable">
                <div class="file-list-wrapper">
                    <div class="file-header">
                        <h1 class="file-title">Active Projects</h1>
                    </div>


                    <div class="search-sort-bar">
                        <div class="search-container">
                            <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" stroke-width="2" aria-hidden="true">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input type="text" placeholder="Search active projects..." class="search-bar"
                                aria-label="Search active projects" v-model="searchQuery">
                        </div>

                        <div class="controls-container">
                            <!-- Sort Dropdown -->
                            <div class="sort-wrapper" v-if="viewMode === 'grid'">
                                <label for="sortSelect" class="sort-label">Sort by:</label>
                                <select id="sortSelect" v-model="sortBy" class="sort-select">
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="name_asc">Name (A-Z)</option>
                                    <option value="name_desc">Name (Z-A)</option>
                                </select>
                            </div>

                            <!-- View Toggles -->
                            <div class="view-toggles" role="group" aria-label="View">
                                <button :class="['btn-toggle', { active: viewMode === 'grid' }]"
                                    @click="viewMode = 'grid'" title="Grid View" aria-label="Grid View"
                                    :aria-pressed="viewMode === 'grid' ? 'true' : 'false'">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <rect x="3" y="3" width="7" height="7" />
                                        <rect x="14" y="3" width="7" height="7" />
                                        <rect x="14" y="14" width="7" height="7" />
                                        <rect x="3" y="14" width="7" height="7" />
                                    </svg>
                                </button>
                                <button :class="['btn-toggle', { active: viewMode === 'list' }]"
                                    @click="viewMode = 'list'" title="List View" aria-label="List View"
                                    :aria-pressed="viewMode === 'list' ? 'true' : 'false'">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        stroke-width="2" aria-hidden="true">
                                        <line x1="8" y1="6" x2="21" y2="6" />
                                        <line x1="8" y1="12" x2="21" y2="12" />
                                        <line x1="8" y1="18" x2="21" y2="18" />
                                        <line x1="3" y1="6" x2="3.01" y2="6" />
                                        <line x1="3" y1="12" x2="3.01" y2="12" />
                                        <line x1="3" y1="18" x2="3.01" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Loading / error / empty -->
                    <div v-if="loading && !processedFiles.length" class="projects-state" role="status">
                        Loading active projects…
                    </div>
                    <div v-else-if="loadError" class="projects-state is-error" role="alert">
                        <p>{{ loadError }}</p>
                        <button type="button" class="btn-state" @click="fetchData(currentPage)">Try Again</button>
                    </div>
                    <div v-else-if="!processedFiles.length" class="projects-state">
                        <template v-if="searchQuery">
                            <p>No active projects match “{{ searchQuery }}”.</p>
                            <button type="button" class="btn-state" @click="searchQuery = ''">Clear Search</button>
                        </template>
                        <template v-else>
                            <p>No active projects yet. When you mark a bid as won, it appears here.</p>
                            <router-link class="btn-state" :to="{ name: 'BidListing', query: { tab: 'EXPIRED' } }">
                                View Bid Details
                            </router-link>
                        </template>
                    </div>

                    <!-- Premium Project Grid -->
                    <div v-else-if="viewMode === 'grid'" class="project-premium-grid">
                        <router-link v-for="pdf in processedFiles" :key="pdf.id" class="project-card-premium"
                            :to="detailsRoute(pdf.id)">
                            <div class="project-card-top">
                                <div class="project-icon-large" aria-hidden="true">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                    </svg>
                                </div>
                            </div>

                            <h3 class="project-name-premium">{{ stripExtension(pdf.project_name) }}</h3>
                            <p class="project-meta-premium">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                Submission date: {{ formatDate(pdf.last_submission_date) }}
                            </p>
                        </router-link>
                    </div>

                    <!-- Table View -->
                    <div v-else class="table-container premium-table-container">
                        <table class="file-table premium-table">
                            <thead>
                                <tr>
                                    <th @click="toggleSort('name')" class="sortable-header">Project Details</th>
                                    <th @click="toggleSort('date')" class="sortable-header">Submission Date</th>
                                    <th class="sortable-header th-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="pdf in processedFiles" :key="pdf.id" class="premium-table-row"
                                    @click="openFromRow($event, pdf.id)">
                                    <td>
                                        <div class="table-project-details">
                                            <div class="table-project-icon" aria-hidden="true">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                                </svg>
                                            </div>
                                            <div class="table-project-info">
                                                <router-link class="file-name-text" :to="detailsRoute(pdf.id)"
                                                    :title="stripExtension(pdf.project_name)">{{ truncateName(pdf.project_name) }}</router-link>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span class="text-date">{{ formatDate(pdf.last_submission_date) }}</span></td>
                                    <td class="td-actions">
                                        <div class="row-actions">
                                            <router-link class="btn-row primary" :to="detailsRoute(pdf.id)">Details</router-link>
                                            <router-link v-if="canAdmin" class="btn-row secondary"
                                                :to="{ name: 'CompanyAdmin', query: { tab: 'obligations', project_id: pdf.id, from: 'active' } }">
                                                Compensation Claims
                                            </router-link>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <AppPagination
                        v-if="processedFiles.length > 0"
                        :currentPage="currentPage"
                        :totalPages="totalPages"
                        @page-changed="fetchData"
                    />
                </div>
            </section>
        </main>
    </div>
</template>

<script>
import axios from 'axios';
import AppSidebar from './Sidebar.vue';
import AppPagination from './AppPagination.vue';
import { isAdmin } from '@/utils/auth';

export default {
    name: 'ActiveProjects',
    components: {
        AppSidebar,
        AppPagination
    },
    props: {
        getAccessToken: Function,
    },
    data() {
        return {
            apiResponseData: [],
            searchQuery: "",
            activeTab: "WIN",
            viewMode: "list",
            sortBy: "newest",
            currentPage: 1,
            totalPages: 1,
            loading: true,
            loadError: '',
        }
    },
    mounted() {
        this.fetchData();
    },
    watch: {
        activeTab() { this.fetchData(1); },
        searchQuery() { this.fetchData(1); },
        sortBy() { this.fetchData(1); }
    },
    computed: {
        processedFiles() {
            return this.apiResponseData || [];
        },
        // Compensation Claims live in the Admin Panel, which only admins can open.
        canAdmin() {
            return isAdmin();
        }
    },
    methods: {
        fetchData: function (page = 1) {
            this.currentPage = page;
            this.loading = true;
            this.loadError = '';
            const url = this.$API_URL + `/user/get-pdf-list?page=${page}&page_size=10&status=${this.activeTab}&search=${encodeURIComponent(this.searchQuery)}&sort_by=${this.sortBy}`;
            axios.get(url, {
                headers: { 'Authorization': 'Bearer ' + this.getAccessToken() }
            }).then((response) => {
                this.apiResponseData = Array.isArray(response.data) ? response.data : (response.data.items || []);
                this.totalPages = response.data.total_pages || 1;
            }).catch((e) => {
                console.error(e);
                this.apiResponseData = [];
                this.loadError = 'Couldn’t load active projects. Check your connection and try again.';
            }).finally(() => {
                this.loading = false;
            });
        },
        // A project's home is its Bid Details page (Project tab and all).
        detailsRoute(pdfId) {
            return { name: 'BidDetails', params: { pdf_id: pdfId }, query: { status: 'WIN' } };
        },
        // Whole-row click for pointer users; the title link covers keyboard users.
        openFromRow(event, pdfId) {
            if (event.target.closest('a, button')) return;
            this.$router.push(this.detailsRoute(pdfId));
        },
        stripExtension(name) {
            if (!name) return 'Untitled Project';
            let cleanName = name.replace(/\.pdf/gi, ""); // Remove every .pdf (incl. comma-joined names)
            return cleanName.replace(/[-_]/g, " "); // Replace - and _ with space
        },
        truncateName(name, limit = 50) {
            const clean = this.stripExtension(name);
            return clean.length > limit ? clean.slice(0, limit) + '...' : clean;
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric'
            });
        },
        toggleSort(column) {
            if (column === 'name') this.sortBy = (this.sortBy === 'name_asc') ? 'name_desc' : 'name_asc';
            else if (column === 'date') this.sortBy = (this.sortBy === 'date_desc') ? 'date_asc' : 'date_desc';
        },
    }
}
</script>

<style scoped>
/* Standard styles reused */
.search-sort-bar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 2rem; }
.search-container { flex: 1; min-width: 250px; }
.controls-container { display: flex; align-items: center; gap: 1rem; }
.sort-wrapper { display: flex; align-items: center; gap: 0.5rem; }
.sort-select { padding: 0.5rem 2rem 0.5rem 1rem; border-radius: 10px; border: 1px solid #e2e8f0; background-color: #fff; cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%231e293b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 0.5rem center; background-size: 1rem; }
.view-toggles { display: flex; background: #f1f5f9; border-radius: 10px; padding: 4px; border: 1px solid #e2e8f0; gap: 4px; }
.btn-toggle { background: transparent; border: none; color: #475467; padding: 6px; border-radius: 6px; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; }
.btn-toggle.active { background: #0071C5; color: #fff; }
.btn-toggle:focus-visible { outline: 2px solid #0071C5; outline-offset: 2px; }
.table-container { width: 100%; overflow-x: auto; background: rgba(255, 255, 255, 0.5); border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.3); backdrop-filter: blur(10px); }
.file-table { width: 100%; border-collapse: collapse; text-align: left; }
.file-table th { text-align: left; padding: 1rem; color: var(--text-secondary); font-size: var(--fs-th); font-weight: var(--fw-th); text-transform: uppercase; letter-spacing: var(--tracking-th); border-bottom: 1px solid rgba(0, 0, 0, 0.05); }
.file-table td { text-align: left; padding: 1rem; border-bottom: 1px solid rgba(0, 0, 0, 0.05); }
.file-name-text { font-weight: 600; color: #0b1220; text-decoration: none; border-radius: 6px; }
.file-name-text:hover { color: #0071C5; text-decoration: underline; }
.file-name-text:focus-visible { outline: 2px solid #0071C5; outline-offset: 2px; }
.text-date { font-size: 0.85rem; font-weight: 500; color: #475569; }
/* Actions column: header and cells share one alignment, declared on both
   sides so neither can drift. */
.th-actions,
.td-actions { text-align: right; }
.th-actions { cursor: default; }

/* Row actions */
.row-actions { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; }
.btn-row {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.75rem;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s ease, color 0.2s ease;
}
.btn-row.primary { background: #0071C5; color: #fff; border: 1px solid #0071C5; }
.btn-row.primary:hover { background: #005a9e; border-color: #005a9e; }
.btn-row.secondary { background: #fff; color: #0071C5; border: 1px solid #0071C5; }
.btn-row.secondary:hover { background: #eef6fc; }
.btn-row:focus-visible { outline: 2px solid #0071C5; outline-offset: 2px; }

/* Loading / error / empty */
.projects-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.85rem;
    padding: 3rem 1.5rem;
    border: 1px dashed #d0d5dd;
    border-radius: 16px;
    background: #f8fafc;
    color: #475467;
    font-size: 0.95rem;
    text-align: center;
}
.projects-state p { margin: 0; }
.projects-state.is-error { color: #b42318; border-color: #fecdca; background: #fef3f2; }
.btn-state {
    display: inline-flex;
    align-items: center;
    padding: 0.55rem 1.1rem;
    border-radius: 10px;
    border: 1px solid #0071C5;
    background: #0071C5;
    color: #fff;
    font-weight: 600;
    font-size: 0.85rem;
    text-decoration: none;
    cursor: pointer;
}
.btn-state:hover { background: #005a9e; border-color: #005a9e; }
.btn-state:focus-visible { outline: 2px solid #0071C5; outline-offset: 2px; }

/* Premium Project Cards */
.project-premium-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-top: 1rem;
}

.project-card-premium {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 1);
    border-radius: 16px;
    padding: 20px;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    min-height: 180px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.03);
    color: inherit;
    text-decoration: none;
}

.project-card-premium:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 25px rgba(0, 113, 197, 0.15);
    border-color: rgba(0, 113, 197, 0.4);
}

.project-card-premium:focus-visible {
    outline: 2px solid #0071C5;
    outline-offset: 2px;
}

.project-card-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
}

.project-icon-large {
    color: #0071C5;
    background: rgba(0, 113, 197, 0.1);
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.project-name-premium {
    font-size: 1.15rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.project-meta-premium {
    font-size: 0.8rem;
    color: #64748b;
    display: flex;
    align-items: center;
    gap: 6px;
    margin: auto 0 0 0;
}

/* Premium Table View Additions */
.premium-table-container {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 1);
    box-shadow: 0 4px 15px rgba(0,0,0,0.03);
    border-radius: 12px;
    overflow: hidden;
}

.premium-table {
    width: 100%;
}

.premium-table th {
    padding: 1.25rem 1.5rem;
    color: var(--text-secondary);
    font-weight: var(--fw-th);          /* 600 */
    text-transform: uppercase;
    font-size: var(--fs-th);            /* 12px */
    letter-spacing: var(--tracking-th); /* 0.08em */
    border-bottom: 2px solid #e2e8f0;
    background: transparent;
}

.premium-table-row {
    cursor: pointer;
    transition: all 0.2s;
}

.premium-table-row:hover {
    background: rgba(241, 245, 249, 0.8);
}

.premium-table td {
    padding: 1.25rem 1.5rem;
    vertical-align: middle;
    border-bottom: 1px solid #f1f5f9;
}

.table-project-details {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.table-project-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(0, 113, 197, 0.1);
    color: #0071C5;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.table-project-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
</style>
